import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { LanguageProvider } from './context/LanguageContext';
import { ThemeProvider } from './context/ThemeContext';
import { Toaster } from './components/ui/sonner';
import Navbar from './components/Navbar';
import AnnouncementBanner from './components/AnnouncementBanner';
import { useSSE } from './hooks/useSSE';
import './App.css';

// Pages
import Login from './pages/Login';
import Register from './pages/Register';
import Dashboard from './pages/Dashboard';
import Markets from './pages/Markets';
import MarketDetail from './pages/MarketDetail';
import CreateMarket from './pages/CreateMarket';
import Profile from './pages/Profile';
import Friends from './pages/Friends';
import UserProfile from './pages/UserProfile';
import Settlements from './pages/Settlements';
import Organizations from './pages/Organizations';
import AdminDashboard from './pages/AdminDashboard';
import JoinGroup from './pages/JoinGroup';
import PendingConfirmation from './pages/PendingConfirmation';
import VerifyEmail from './pages/VerifyEmail';
import Impressum from './pages/Impressum';
import Landing from './pages/Landing';
import Datenschutz from './pages/Datenschutz';
import Footer from './components/Footer';

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-pulse text-muted-foreground">Lädt...</div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return children;
};

// Verified Route Component (requires logged in AND verified email)
const VerifiedRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-pulse text-muted-foreground">Lädt...</div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (!user.email_verified) {
    return <Navigate to="/verify-email" replace />;
  }

  if (!user.is_confirmed && !user.is_admin) {
    return <PendingConfirmation />;
  }

  return children;
};

// Public Route (redirect to dashboard if logged in)
const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-pulse text-muted-foreground">Lädt...</div>
      </div>
    );
  }

  if (user) {
    return <Navigate to="/markets" replace />;
  }

  return children;
};

// Startseite: eingeloggt -> Märkte, sonst öffentliche Landing-Page
const RootRoute = () => {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-pulse text-muted-foreground">Lädt...</div>
      </div>
    );
  }
  return user ? <Navigate to="/markets" replace /> : <Landing />;
};

function AppRoutes() {
  useSSE(); // Activate real-time events
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/impressum" element={<Impressum />} />
      <Route path="/datenschutz" element={<Datenschutz />} />
      <Route
        path="/login"
        element={
          <PublicRoute>
            <Login />
          </PublicRoute>
        }
      />
      <Route
        path="/register"
        element={
          <PublicRoute>
            <Register />
          </PublicRoute>
        }
      />
      <Route
        path="/verify-email"
        element={
          <ProtectedRoute>
            <VerifyEmail />
          </ProtectedRoute>
        }
      />

      {/* Protected & Verified Routes */}
      <Route
        path="/dashboard"
        element={
          <VerifiedRoute>
            <Dashboard />
          </VerifiedRoute>
        }
      />
      <Route
        path="/markets"
        element={
          <VerifiedRoute>
            <Markets />
          </VerifiedRoute>
        }
      />
      <Route
        path="/market/:id"
        element={
          <VerifiedRoute>
            <MarketDetail />
          </VerifiedRoute>
        }
      />
      <Route
        path="/create-market"
        element={
          <VerifiedRoute>
            <CreateMarket />
          </VerifiedRoute>
        }
      />
      <Route
        path="/profile"
        element={
          <VerifiedRoute>
            <Profile />
          </VerifiedRoute>
        }
      />
      <Route
        path="/friends"
        element={
          <VerifiedRoute>
            <Friends />
          </VerifiedRoute>
        }
      />
      <Route
        path="/settlements"
        element={
          <VerifiedRoute>
            <Settlements />
          </VerifiedRoute>
        }
      />
      <Route
        path="/organizations"
        element={
          <VerifiedRoute>
            <Organizations />
          </VerifiedRoute>
        }
      />
      <Route
        path="/user/:id"
        element={
          <VerifiedRoute>
            <UserProfile />
          </VerifiedRoute>
        }
      />
      <Route
        path="/join-group/:token"
        element={
          <VerifiedRoute>
            <JoinGroup />
          </VerifiedRoute>
        }
      />
      <Route
        path="/admin"
        element={
          <VerifiedRoute>
            <AdminDashboard />
          </VerifiedRoute>
        }
      />

      {/* Redirect root to markets (PublicRoute/ProtectedRoute will handle the rest) */}
      <Route path="/" element={<RootRoute />} />
      
      {/* 404 - Redirect to markets */}
      <Route path="*" element={<Navigate to="/markets" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <ThemeProvider>
        <LanguageProvider>
          <AuthProvider>
            <div className="app-container min-h-screen flex flex-col">
              <Navbar />
              <AnnouncementBanner />
              <div className="flex-1">
                <AppRoutes />
              </div>
              <Footer />
              <Toaster 
                position="top-right" 
                richColors 
                closeButton 
                toastOptions={{
                  style: {
                    marginTop: 'env(safe-area-inset-top)',
                  },
                }}
              />
            </div>
          </AuthProvider>
        </LanguageProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}

export default App;
